import React, { useCallback, useMemo, useState } from 'react';
import { ADDITIONAL_SERVICES } from '../../constants/columns';
import { useTable, useGlobalFilter, usePagination, useSortBy } from 'react-table';
import { servicesOptions } from '../../constants/filterOptions';
import { useNavigate } from 'react-router-dom';
import axios from '../../plugins/axios';
import checkCrmSidebarNotifications from '../../api/notifications/checkCrmSidebarNotifications';
import { setFilters } from '../../utils/setFilters';
import isUserHavePermission from '../../utils/isUserHavePermission';

// components
import Header from '../../components/Header/Header';
import Table from '../../components/Table/Table';
import TableTabs from '../../components/TableTabs/TableTabs';
import Empty from '../../components/Empty/Empty';
import HookForm from '../../components/HookForm/HookForm';
import Modal from '../../components/Modal/Modal';
import AddServiceForm from './AddServiceForm';

// icons
import icon from '../../brand/generated/sidebar/services.svg';
import getIntegratioPolesPrice from '../../api/shop/getIntegratioPolesPrice';
import { iconBucket } from '../../utils/icons';
import removeService from '../../api/services/removeAdditionalService';
import { INSURANCE_SERVICE_ID_2_5, INSURANCE_SERVICE_ID_3, INSURANCE_SERVICE_ID_3_5 } from '../../constants';
import { PERMISSION_CAN_DELETE_DEALS } from '../../constants/roles';

const tableStyle = { backgroundColor: 'white', marginTop: 10 };
const columns = ADDITIONAL_SERVICES;

const Services = ({
  header = true,
  withCheckboxes,
  submitButton,
  cancelButton,
  onSubmit,
  onCancel,
  submitButtonLabel,
  paymentType,
  edit = true,
  orderId,
  requiredCheckboxes,
  cityId,
  enableInsurance,
  addServiceInModal,
}) => {
  const navigate = useNavigate();
  const [addServiceModalOpen, setAddServiceModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [services, setServices] = React.useState([]);
  const [finansialServices, setFinansialServices] = useState([]);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    hasPreviousPage: false,
    hasNextPage: false,
    pageSize: 25,
  });
  const [isFetchAll, setFetchAll] = useState(false);

  const tableConfig_1 = useMemo(
    () => ({
      columns,
      data: finansialServices,
      initialState: {
        pageSize: 25,
      },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
      disableSortRemove: true,
    }),
    [finansialServices, pagination.totalPages, pagination.page]
  );

  const tableConfig_2 = useMemo(
    () => ({
      columns,
      data: additionalServices,
      initialState: {
        pageSize: 25,
      },
      manualPagination: true,
      pageCount: pagination.totalPages,
      page: pagination.page - 1,
      disableSortRemove: true,
    }),
    [additionalServices, pagination.totalPages, pagination.page]
  );

  const table_1 = useTable(tableConfig_1, useGlobalFilter, useSortBy, usePagination);

  const table_2 = useTable(tableConfig_2, useGlobalFilter, useSortBy, usePagination);

  const { setGlobalFilter, page, setPageSize } = table_1;
  const { page: page2 } = table_2;

  const [value, setValue] = useState('');
  const [queryParams, setQueryParams] = useState({});

  const canDeleteServices = isUserHavePermission(PERMISSION_CAN_DELETE_DEALS);

  const deleteServicesLocal = useCallback((ids) => {
    const idsSet = new Set(ids);
    setAdditionalServices((prev) => prev.filter((item) => !idsSet.has(item?.id)));
    setFinansialServices((prev) => prev.filter((item) => !idsSet.has(item?.id)));
    setServices((prev) => prev.filter((item) => !idsSet.has(item?.id)));
  }, []);

  const handleBulkDelete = useCallback(
    async (rows) => {
      const ids = (rows || []).map((r) => r?.id).filter(Boolean);
      if (!ids.length) return;

      setLoading(true);
      const results = await Promise.allSettled(ids.map((id) => removeService({ id })));
      const deletedIds = ids.filter((_, i) => results[i]?.status === 'fulfilled');
      if (deletedIds.length) {
        deleteServicesLocal(deletedIds);
      }
      setLoading(false);
    },
    [deleteServicesLocal]
  );

  const getServices = useCallback(
    async (params) => {
      setLoading(true);
      let ids = [];
      await axios
        .get('/api/crm/additional-services/getAdditionalServices', { params: { ...params, cityId } })
        .then(async (res) => {
          res.data.additionalServices.forEach((item) => {
            item.notifications.forEach((i) => {
              ids.push(i.id);
            });
          });

          let newData = res?.data?.additionalServices;

          if (orderId && enableInsurance) {
            newData = await Promise.all(
              res?.data?.additionalServices.map(async (eq) => {
                if (
                  eq.id == INSURANCE_SERVICE_ID_2_5 ||
                  eq.id == INSURANCE_SERVICE_ID_3 ||
                  eq.id == INSURANCE_SERVICE_ID_3_5
                ) {
                  try {
                    const res = await getIntegratioPolesPrice({ orderId: orderId?.value, serviceId: eq.id });
                    console.log(res);

                    eq.price = typeof res?.data != 'number' ? 0 : res?.data;
                    return eq;
                  } catch (err) {
                    return eq;
                  }
                }
                return eq;
              })
            );
          }

          if (enableInsurance) {
            setFinansialServices(() =>
              newData.filter(
                (service) =>
                  service.id === INSURANCE_SERVICE_ID_2_5 ||
                  service.id === INSURANCE_SERVICE_ID_3 ||
                  service.id === INSURANCE_SERVICE_ID_3_5
              )
            );
          } else {
            setServices(newData);
          }

          setAdditionalServices(() =>
            newData.filter(
              (service) =>
                service.id !== INSURANCE_SERVICE_ID_2_5 &&
                service.id !== INSURANCE_SERVICE_ID_3 &&
                service.id !== INSURANCE_SERVICE_ID_3_5
            )
          );
          setPagination({
            ...res.data.pagination,
            page: res.data.pagination?.pageNumber,
            pageSize: pagination.pageSize,
          });
          setDataCount(res.data.pagination?.count);
        })
        .catch((error) => {})
        .finally(() => setLoading(false));
      if (ids.length > 0) {
        checkCrmSidebarNotifications({ notificationIds: ids })
          .then((res) => {})
          .catch((error) => {});
      }
    },
    [orderId, pagination.pageSize]
  );

  const popover = useMemo(
    () => [
      {
        title: 'Ð£Ð´Ð°Ð»Ð¸ÑÑ',
        icon: iconBucket(),
        onClick: (row) => {
          setLoading(true);
          removeService({ id: row?.id })
            .then(() => deleteServicesLocal([row?.id].filter(Boolean)))
            .finally(() => setLoading(false));
        },
        
      permission: PERMISSION_CAN_DELETE_DEALS,
      },
    ],
    [deleteServicesLocal]
  );

  React.useEffect(() => {
    setPageSize(25);
  }, [setPageSize]);

  React.useEffect(() => {
    if (!isFetchAll) {
      const { pageSize: _, ...restQueryParams } = queryParams;
      getServices({ page: pagination.page, pageSize: pagination.pageSize, ...restQueryParams });
    }
  }, [pagination.page, pagination.pageSize, queryParams, isFetchAll]);

  const handleNavigate = () => {
    if (addServiceInModal) {
      setAddServiceModalOpen(true);
      return;
    }
    navigate('/services/add-service');
  };

  const handleServiceCreated = () => {
    setAddServiceModalOpen(false);
    const { pageSize: _, ...restQueryParams } = queryParams;
    getServices({ page: pagination.page, pageSize: pagination.pageSize, ...restQueryParams });
  };
  const navigateToEdit = (index) => {
    const record = services.filter((item) => item.id === index);
    navigate(`/services/${record[0].id}`);
  };

  const setAllFilters = () => {
    getServices({ all: value });
  };

  const handleActionPopover = async (carIndex, actionIndex, title, id) => {
    console.log(carIndex, actionIndex);
  };

  const effectiveWithCheckboxes = withCheckboxes ?? (!onSubmit && canDeleteServices);
  const effectiveOnSubmit = onSubmit ?? (canDeleteServices ? handleBulkDelete : undefined);
  const effectiveSubmitButtonLabel = submitButtonLabel ?? (!onSubmit && canDeleteServices ? 'Ð£Ð´Ð°Ð»Ð¸ÑÑ Ð²ÑÐ±ÑÐ°Ð½Ð½ÑÐµ' : undefined);

  return (
    <>
      {header && (
        <>
          <Header title="ÐÐ¾Ð¿Ð¾Ð»Ð½Ð¸ÑÐµÐ»ÑÐ½ÑÐµ ÑÑÐ»ÑÐ³Ð¸" icon={icon} />
          <br />
          <br />
          <br />
          <br />
        </>
      )}
      <TableTabs
        setFilter={setGlobalFilter}
        options={servicesOptions}
        value={value}
        filterComponent={
          <HookForm
            inputs={servicesOptions}
            grid={{
              display: 'grid',
              gap: '10px',
              gridTemplateColumns: '1fr 1fr 1fr',
            }}
            responsiveForm
            style={{ backgroundColor: 'transparent' }}
          />
        }
        handleSearchParams={(resetFilters) => {
          setFilters(resetFilters, setFetchAll, setPageSize, setPagination, setQueryParams, servicesOptions);
          setTimeout(() => {
            setPagination((prev) => ({ ...prev, pageSize: 25, page: 1 }));
            setPageSize(25);
          }, 0);
        }}
        setValue={setValue}
        navigate={handleNavigate}
        button={addServiceInModal ? 'ÐÐ¾Ð±Ð°Ð²Ð¸ÑÑ Ð½Ð¾Ð²ÑÑ ÑÑÐ»ÑÐ³Ñ' : 'ÐÐ¾Ð±Ð°Ð²Ð¸ÑÑ ÑÑÐ»ÑÐ³Ñ'}
        inputPlaceholder={'ÐÑÑÑÑÑÐ¹ Ð¿Ð¾Ð¸ÑÐº Ð¿Ð¾ ÑÑÐ»ÑÐ³Ð°Ð¼'}
      />
      <br />
      {!page.length && !page2.length && !loading ? (
        <Empty />
      ) : (
        <>
          {enableInsurance ? (
            <>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '20px',
                  color: 'var(--brand-dark)',
                  margin: '0px 0 20px 0',
                }}
              >
                Ð¤Ð¸Ð½Ð°Ð½ÑÐ¾Ð²ÑÐµ ÑÑÐ»ÑÐ³Ð¸
              </p>
              <Table
                table={table_1}
                all={'ÑÑÐ»ÑÐ³'}
                sort
                style={tableStyle}
                loading={loading}
                p={pagination}
                pagination={true}
                setPagination={setPagination}
                setFetchAll={setFetchAll}
                isFetchAll={isFetchAll}
                setData={setFinansialServices}
                setLoading={setLoading}
                queryParams={queryParams}
                api={'/api/crm/additional-services/getAdditionalServices'}
                response={'additionalServices'}
                isSortable
                withCheckboxes={effectiveWithCheckboxes}
                submitButton={submitButton}
                cancelButton={cancelButton}
                onSubmit={effectiveOnSubmit}
                onCancel={onCancel}
                submitButtonLabel={effectiveSubmitButtonLabel}
                paymentType={paymentType}
                dataCount={finansialServices.length}
                edit={edit}
                requiredCheckboxes={requiredCheckboxes}
                popover={popover}
                handleActionPopover={handleActionPopover}
                handleNavigate={navigateToEdit}
                navigateOnRowClick={!onSubmit}
              />
            </>
          ) : (
            <>
              <p
                style={{
                  fontWeight: 700,
                  fontSize: '20px',
                  lineHeight: '20px',
                  color: 'var(--brand-dark)',
                  margin: '40px 0 20px 0',
                }}
              >
                ÐÐ¾Ð¿Ð¾Ð»Ð½Ð¸ÑÐµÐ»ÑÐ½ÑÐ¹ ÑÐµÑÐ²Ð¸Ñ
              </p>
              <Table
                table={table_2}
                all={'ÑÑÐ»ÑÐ³'}
                sort
                style={tableStyle}
                loading={loading}
                p={pagination}
                pagination={true}
                setPagination={setPagination}
                setFetchAll={setFetchAll}
                isFetchAll={isFetchAll}
                setData={setAdditionalServices}
                setLoading={setLoading}
                queryParams={queryParams}
                api={'/api/crm/additional-services/getAdditionalServices'}
                response={'additionalServices'}
                isSortable
                withCheckboxes={effectiveWithCheckboxes}
                submitButton={submitButton}
                cancelButton={cancelButton}
                onSubmit={effectiveOnSubmit}
                onCancel={onCancel}
                submitButtonLabel={effectiveSubmitButtonLabel}
                paymentType={paymentType}
                dataCount={additionalServices.length}
                edit={edit}
                requiredCheckboxes={requiredCheckboxes}
                popover={popover}
                handleActionPopover={handleActionPopover}
                handleNavigate={navigateToEdit}
                navigateOnRowClick={!onSubmit}
              />
            </>
          )}
        </>
      )}
      {addServiceInModal && (
        <Modal
          isOpen={addServiceModalOpen}
          onRequestClose={() => setAddServiceModalOpen(false)}
          title="ÐÐ¾Ð²Ð°Ñ ÑÑÐ»ÑÐ³Ð°"
          overlayStyle={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          contentStyle={{
            position: 'relative',
            inset: 'auto',
            top: 'auto',
            left: 'auto',
            right: 'auto',
            bottom: 'auto',
            width: '90%',
            maxWidth: 640,
            maxHeight: '90vh',
            overflow: 'auto',
          }}
        >
          <AddServiceForm defaultCityId={cityId} onSuccess={handleServiceCreated} />
        </Modal>
      )}
    </>
  );
};

export default Services;
